// src/admin/components/DataTable.tsx
import type { ReactNode } from 'react';
import styles from './DataTable.module.css';

export interface Column<R> {
  key: string;
  header: ReactNode;
  render: (row: R) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

interface Props<R> {
  rows: R[] | undefined;
  columns: Column<R>[];
  isLoading?: boolean;
  error?: string | null;
  onRowClick?: (row: R) => void;
  emptyLabel?: string;
  rowKey: (row: R) => string;
}

export default function DataTable<R>({
  rows, columns, isLoading, error, onRowClick, emptyLabel = 'No rows', rowKey,
}: Props<R>) {
  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} style={{ width: c.width, textAlign: c.align ?? 'left' }}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr><td colSpan={columns.length} className={styles.stateRow}>Loading…</td></tr>
          )}
          {error && !isLoading && (
            <tr><td colSpan={columns.length} className={`${styles.stateRow} ${styles.err}`}>{error}</td></tr>
          )}
          {!isLoading && !error && rows && rows.length === 0 && (
            <tr><td colSpan={columns.length} className={styles.stateRow}>{emptyLabel}</td></tr>
          )}
          {!isLoading && !error && rows?.map(row => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? styles.clickable : undefined}
            >
              {columns.map(c => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
